import { useState, useEffect } from 'react';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { cleanCorruptedText, cleanJSONString } from '../utils/planDataUtils';

export const usePlanDataLoader = (
  currentPlanId,
  isCreatingStrategy,
  isStrategyCreationMode,
  planState,
  planLoadInitialized,
  hasStrategyBeenCreated,
  shortSummary,
  finalProgramSequence,
  location,
  stateSetters
) => {
  // Initialize loading state to false to prevent immediate loading screen
  const [isLoading, setIsLoading] = useState(false);
  const [lastLoadedPlanId, setLastLoadedPlanId] = useState(null);
  // FIX #42: Track which plan's forceReload we've already consumed to prevent repeated reloads
  const [consumedForceReloadForPlan, setConsumedForceReloadForPlan] = useState(null);

  useEffect(() => {
    const loadPlanData = async () => {
      // Don't load if state setters aren't available yet
      if (!stateSetters || Object.keys(stateSetters).length === 0) {
        console.log("Skipping load - state setters not available yet");
        return;
      }

      // CRITICAL: Never load data for new plans or during strategy creation
      if (!currentPlanId) {
        console.log("Skipping load - no current plan ID (new plan)");
        return;
      }

      // CRITICAL: Don't load if we just completed strategy creation to prevent overwriting
      const timeSinceCompletion = Date.now() - (window.strategyCompletionTime || 0);
      if (timeSinceCompletion < 45000 && (hasStrategyBeenCreated || window.strategyJustCompleted)) {
        console.log("Skipping load - recent strategy completion, protecting fresh data for", Math.round((45000 - timeSinceCompletion)/1000), "more seconds");
        return;
      }

      // FIX #42: Only use forceReload ONCE per plan - don't let it persist and cause repeated reloads
      const shouldForceReload = location?.state?.forceReload && consumedForceReloadForPlan !== currentPlanId;
      
      // Check if we're switching to a different plan - only when explicitly switching
      const isPlannSwitch = (lastLoadedPlanId && lastLoadedPlanId !== currentPlanId) ||
                           shouldForceReload;
      
      // CRITICAL: Don't load if we already have this plan's data loaded
      // TEMPORARILY DISABLED to force reload and get missing Experience Design Principles
      // if (lastLoadedPlanId === currentPlanId && hasStrategyBeenCreated && shortSummary && finalProgramSequence?.length > 0) {
      //   console.log("Skipping load - plan", currentPlanId, "already loaded with", finalProgramSequence.length, "programs");
      //   return;
      // }

      // Don't load if we already have meaningful data UNLESS we're switching plans
      if (!isPlannSwitch && hasStrategyBeenCreated && shortSummary && finalProgramSequence?.length > 0) {
        console.log("Skipping load - strategy data already exists with", finalProgramSequence.length, "programs");
        return;
      }

      // Force load when switching plans - clear previous state
      if (isPlannSwitch) {
        console.log("Force loading due to plan switch - currentPlanId:", currentPlanId, "lastLoaded:", lastLoadedPlanId);
        // Only clear state if we're actually switching to a different plan
        if (lastLoadedPlanId !== currentPlanId) {
          if (stateSetters.setPlanLoadInitialized) {
            stateSetters.setPlanLoadInitialized(false);
          }
          if (stateSetters.setIsInitialLoad) {
            stateSetters.setIsInitialLoad(true);
          }
          if (stateSetters.setHasStrategyBeenCreated) {
            stateSetters.setHasStrategyBeenCreated(false);
          }
          if (stateSetters.setIsSaved) {
            stateSetters.setIsSaved(true); // Mark as saved during switch to prevent auto-save
          }
        }
      }

      // Only load if we have a valid plan ID and are not creating a new strategy
      if (!currentPlanId || isCreatingStrategy || isStrategyCreationMode) {
        console.log("Skipping load - no plan ID or strategy in progress");
        return;
      }

      // CRITICAL: Skip if already saving or loading to prevent conflicts
      if (planState === "saving" || planState === "loading") {
        console.log("Skipping load - currently saving or loading");
        return;
      }

      // CRITICAL: If plan is already initialized and we have data, don't reload
      // But also check if experienceDesignPrinciples is missing and needs to be loaded
      // FIX #29: Allow forceReload to bypass this guard so enabled_features can be loaded
      if (!isPlannSwitch && planLoadInitialized && (shortSummary || finalProgramSequence?.length > 0)) {
        console.log("Skipping load - plan already initialized with data");
        return;
      }

      // Additional check to prevent loading if we're already in a good state AND not switching plans
      if (!isPlannSwitch && planState === "saved" && shortSummary && finalProgramSequence?.length > 0) {
        console.log("Skipping load - plan already in saved state with data");
        return;
      }

      // CRITICAL FIX: Don't reload if we just loaded this plan successfully
      const loadTimestamp = window[`planLoadTime_${currentPlanId}`] || 0;
      const timeSinceLoad = Date.now() - loadTimestamp;
      if (timeSinceLoad < 10000 && planState === "saved") {
        console.log("Skipping load - recently loaded this plan, time since load:", timeSinceLoad, "ms");
        return;
      }

      // EMERGENCY BRAKE: If we've been loading continuously, stop
      const loadKey = `planLoad_${currentPlanId}`;
      const lastLoadTime = window[loadKey] || 0;
      const timeSinceLastLoad = Date.now() - lastLoadTime;
      if (timeSinceLastLoad < 5000) { // Increased from 2s to 5s to prevent rapid reloads
        console.log("Skipping load - too frequent loading attempts, preventing loop. Last load was", timeSinceLastLoad, "ms ago");
        return;
      }
      window[loadKey] = Date.now();

      console.log("Loading existing plan data for ID:", currentPlanId);

      // Set loading state immediately, before any other operations
      setIsLoading(true);

      if (stateSetters.setPlanLoadInitialized) {
        stateSetters.setPlanLoadInitialized(true);
      }

      if (stateSetters.setPlanState) {
        stateSetters.setPlanState("loading");
      }

      // Initialize hasGoodData outside try block for scope access
      let hasGoodData = false;

      try {
        const response = await fetchWithAuth(`/api/plans/${currentPlanId}`);
        if (!response.ok) {
          throw new Error(`Failed to fetch plan: ${response.status}`);
        }

        const planData = await response.json();
        console.log("Loaded plan data:", {
          id: planData.id,
          hasShortSummary: !!planData.short_summary,
          selectedProgramsType: typeof planData.selected_programs,
          selectedProgramsSize: planData.selected_programs?.length || 0,
        });

        // EMERGENCY: Detect massive corruption early
        const selectedSize = planData.selected_programs?.length || 0;
        const sequenceSize = planData.final_program_sequence?.length || 0;

        if (selectedSize > 1000000 || sequenceSize > 1000000) {
          console.error("MASSIVE CORRUPTION DETECTED - resetting to empty arrays", { selectedSize, sequenceSize });

          // Clear corrupted data immediately
          if (stateSetters.setSelectedPrograms) stateSetters.setSelectedPrograms([]);
          if (stateSetters.setFinalProgramSequence) stateSetters.setFinalProgramSequence([]);
          if (stateSetters.setTotalDuration) stateSetters.setTotalDuration(0);
          if (stateSetters.setIsSaved) stateSetters.setIsSaved(true);
          if (stateSetters.setHasStrategyBeenCreated) stateSetters.setHasStrategyBeenCreated(true);
          if (stateSetters.setPlanState) stateSetters.setPlanState("saved");

          return;
        }

        // Check for potential data corruption but don't block loading
        const isEmptyPrograms = (!planData.selected_programs || planData.selected_programs === "[]") && 
                               (!planData.final_program_sequence || planData.final_program_sequence === "[]");

        if (isEmptyPrograms && planData.short_summary && planData.total_duration > 0) {
          console.warn("⚠️ POTENTIAL DATA CORRUPTION: Empty programs with existing plan content", {
            planId: currentPlanId,
            hasShortSummary: !!planData.short_summary,
            totalDuration: planData.total_duration,
            selectedProgramsRaw: planData.selected_programs,
            finalSequenceRaw: planData.final_program_sequence,
            // This is likely legitimate for plans that haven't had programs added yet
          });
        }

        // Parse and validate data
        let selectedPrograms = [];
        let objectives = [];
        let finalSequence = [];

        // Parse selected_programs safely
        if (planData.selected_programs && typeof planData.selected_programs === "string") {
          try {
            console.log("Parsing selected_programs:", {
              rawData: planData.selected_programs.substring(0, 100) + "...",
              length: planData.selected_programs.length
            });

            const cleanedJsonString = cleanJSONString(planData.selected_programs);
            const parsed = JSON.parse(cleanedJsonString);

            console.log("Parsed selected_programs:", {
              isArray: Array.isArray(parsed),
              length: parsed?.length,
              firstItem: parsed?.[0]
            });

            if (Array.isArray(parsed)) {
              if (parsed.length === 0) {
                console.log("Selected programs array is empty");
                selectedPrograms = [];
              } else if (parsed.length > 50) {
                console.warn(`Very large array detected: ${parsed.length} items - limiting to 10`);
                selectedPrograms = parsed.slice(0, 10);
              } else {
                selectedPrograms = parsed
                  .filter((item) => {
                    const isValid = item && typeof item === "object" && 
                           (item.program_of_work || item.ProgramName || item.name);
                    if (!isValid) {
                      console.warn("Filtering out invalid program:", item);
                    }
                    return isValid;
                  })
                  .slice(0, 10)
                  .map((program) => ({
                    program_of_work: String(program.program_of_work || program.ProgramName || program.name).substring(0, 200),
                    phase: String(program.phase || "Discovery").substring(0, 50),
                    goal: String(program.goal || "").substring(0, 500),
                    description: String(program.description || "").substring(0, 1000),
                    totalProgramLength: Math.min(Number(program.totalProgramLength) || 40, 1000),
                    isIncluded: (() => {
                      const hasProperty = program.hasOwnProperty('isIncluded');
                      const originalValue = program.isIncluded;
                      // CRITICAL: Preserve the exact boolean value from database - only default to true if property doesn't exist
                      const finalValue = hasProperty ? originalValue : true;
                      console.log(`🔄 Loading program ${program.program_of_work || program.name}: isIncluded hasProperty=${hasProperty}, originalValue=${originalValue} (${typeof originalValue}), finalValue=${finalValue} (${typeof finalValue})`);
                      return finalValue;
                    })(),
                    methods: Array.isArray(program.methods) ? program.methods.slice(0, 5) : 
                             Array.isArray(program.related_methods) ? program.related_methods.slice(0, 5) : [],
                    Stages: Array.isArray(program.Stages) ? program.Stages.slice(0, 5) : 
                            Array.isArray(program.stages) ? program.stages.slice(0, 5) : [],
                    toolsByStage: program.toolsByStage || {},
                  }));

                console.log("Final selected programs:", {
                  count: selectedPrograms.length,
                  programs: selectedPrograms.map(p => p.program_of_work)
                });
              }
            }
          } catch (e) {
            console.error("Failed to parse selectedPrograms:", e.message, planData.selected_programs.substring(0, 200));
            selectedPrograms = [];
          }
        }

        // Parse final_program_sequence safely
        if (planData.final_program_sequence && typeof planData.final_program_sequence === "string") {
          try {
            console.log("Parsing final_program_sequence:", {
              rawData: planData.final_program_sequence.substring(0, 100) + "...",
              length: planData.final_program_sequence.length
            });

            const cleanedJsonString = cleanJSONString(planData.final_program_sequence);
            const parsed = JSON.parse(cleanedJsonString);

            console.log("Parsed final_program_sequence:", {
              isArray: Array.isArray(parsed),
              length: parsed?.length,
              firstItem: parsed?.[0]
            });

            if (Array.isArray(parsed)) {
              if (parsed.length === 0) {
                console.log("Final program sequence array is empty");
                finalSequence = [];
              } else if (parsed.length > 50) {
                console.warn(`Very large array detected: ${parsed.length} items - limiting to 10`);
                finalSequence = parsed.slice(0, 10);
              } else {
                const validPrograms = parsed.filter((item) => {
                  const isValid = item && typeof item === "object" && 
                           (item.program_of_work || item.ProgramName || item.name);
                  if (!isValid) {
                    console.warn("Filtering out invalid final program:", item);
                  }
                  return isValid;
                });

                // Keep the full program structure when loading
                finalSequence = validPrograms.slice(0, 10).map((program) => ({
                  program_of_work: String(program.program_of_work || program.ProgramName || program.name).substring(0, 200),
                  phase: String(program.phase || "Discovery").substring(0, 50),
                  goal: String(program.goal || "").substring(0, 500),
                  description: String(program.description || "").substring(0, 1000),
                  totalProgramLength: Math.min(Number(program.totalProgramLength) || 40, 1000),
                  isIncluded: program.hasOwnProperty('isIncluded') ? program.isIncluded : true, // Preserve exact boolean value from database without conversion
                  methods: Array.isArray(program.methods) ? program.methods.slice(0, 5) : 
                           Array.isArray(program.related_methods) ? program.related_methods.slice(0, 5) : [],
                  Stages: Array.isArray(program.Stages) ? program.Stages.slice(0, 5) : 
                          Array.isArray(program.stages) ? program.stages.slice(0, 5) : [],
                  toolsByStage: program.toolsByStage || {},
                  expectedDeliverables: Array.isArray(program.expectedDeliverables) ? program.expectedDeliverables : [],
                }));

                console.log("Final program sequence loaded:", {
                  count: finalSequence.length,
                  programs: finalSequence.map(p => ({
                    name: p.program_of_work,
                    phase: p.phase,
                    duration: p.totalProgramLength,
                    isIncluded: p.isIncluded,
                    rawIsIncluded: p.isIncluded // Log the raw value from DB
                  }))
                });
                
                console.log("🔍 Raw program data from database:", parsed.map(p => ({
                  name: p.program_of_work || p.name,
                  hasIsIncludedProperty: p.hasOwnProperty('isIncluded'),
                  rawIsIncluded: p.isIncluded,
                  isIncludedType: typeof p.isIncluded
                })));
              }
            }
          } catch (e) {
            console.error("Failed to parse finalProgramSequence:", e.message, planData.final_program_sequence.substring(0, 200));
            finalSequence = [];
          }
        }

        const parseNestedStringArray = (value) => {
          let current = value;
          for (let i = 0; i < 5; i++) {
            if (typeof current !== "string") break;
            const trimmed = current.trim();
            if (!trimmed) return [];
            try {
              current = JSON.parse(trimmed);
            } catch {
              // Line-separated text already holds whole statements, which can
              // contain commas. Only single-line legacy lists split on commas.
              return trimmed
                .replace(/^\[|\]$/g, "")
                .split(trimmed.includes("\n") ? /\n/ : /\n|,(?=\s*[-"A-Za-z0-9])/)
                .map((obj) => obj.replace(/^[-•\s"]*/, "").replace(/["]*$/, "").trim())
                .filter(Boolean);
            }
          }
          if (Array.isArray(current) && current.length === 1 && typeof current[0] === "string" && current[0].trim().startsWith("[")) {
            return parseNestedStringArray(current[0]);
          }
          if (Array.isArray(current)) {
            return current
              .flatMap((obj) => Array.isArray(obj) ? obj : [obj])
              .map((obj) => String(obj).replace(/^\["|"\]$|^"|"$/g, "").replace(/^[-•\s]*/, "").trim())
              .filter(Boolean);
          }
          return current ? [String(current).replace(/^\["|"\]$|^"|"$/g, "").replace(/^[-•\s]*/, "").trim()].filter(Boolean) : [];
        };

        // Parse objectives safely, including legacy double/triple-encoded values
        if (planData.objectives_statement && typeof planData.objectives_statement === "string") {
          objectives = parseNestedStringArray(planData.objectives_statement);
        } else if (Array.isArray(planData.objectives_statement)) {
          objectives = parseNestedStringArray(planData.objectives_statement);
        }

        // Use the best available program data - prioritize finalSequence, fallback to selectedPrograms
        const programs = finalSequence.length > 0 ? finalSequence : 
                        selectedPrograms.length > 0 ? selectedPrograms : [];

        console.log("Program data selection:", {
          finalSequenceCount: finalSequence.length,
          selectedProgramsCount: selectedPrograms.length,
          usingPrograms: programs.length,
          programPreview: programs.slice(0, 2).map(p => ({
            name: p.program_of_work || p.name,
            phase: p.phase,
            duration: p.totalProgramLength
          }))
        });

        // Note: Removed overly strict validation that was preventing legitimate empty plan loads
        console.log("Loading plan data:", {
          programsFromDb: programs.length,
          existingPrograms: finalProgramSequence?.length || 0,
          shouldLoad: true
        });

        // Parse deliverables and metrics safely
        let deliverables = "";
        let metrics = "";

        if (planData.deliverables_statement) {
          if (typeof planData.deliverables_statement === "string") {
            try {
              const parsed = JSON.parse(planData.deliverables_statement);
              deliverables = Array.isArray(parsed) ? parsed.join("\n") : planData.deliverables_statement;
            } catch (e) {
              deliverables = planData.deliverables_statement;
            }
          } else if (Array.isArray(planData.deliverables_statement)) {
            deliverables = planData.deliverables_statement.join("\n");
          }
        }

        if (planData.metrics_statement) {
          if (typeof planData.metrics_statement === "string") {
            try {
              const parsed = JSON.parse(planData.metrics_statement);
              metrics = Array.isArray(parsed) ? parsed.join("\n") : planData.metrics_statement;
            } catch (e) {
              metrics = planData.metrics_statement;
            }
          } else if (Array.isArray(planData.metrics_statement)) {
            metrics = planData.metrics_statement.join("\n");
          }
        }

        // Calculate total duration from programs - only include enabled programs
        const calculatedTotalDuration = programs.reduce((sum, program) => {
          // Only include programs that are enabled (isIncluded === true)
          if (program.isIncluded === true) {
            return sum + (program.totalProgramLength || program.duration || 0);
          }
          return sum;
        }, 0);

        // Update all state with loaded data
        if (stateSetters.setCurrentDesignChallenge) {
          stateSetters.setCurrentDesignChallenge(cleanCorruptedText(planData.design_challenge || ""));
        }
        if (stateSetters.setPlanName) {
          stateSetters.setPlanName(cleanCorruptedText(planData.plan_name || ""));
        }
        if (stateSetters.setShortSummary) {
          stateSetters.setShortSummary(cleanCorruptedText(planData.short_summary || ""));
        }
        if (stateSetters.setProblemStatement) {
          stateSetters.setProblemStatement(cleanCorruptedText(planData.problem_statement || planData.problemStatement || ""));
        }
        if (stateSetters.setGoalStatement) {
          stateSetters.setGoalStatement(cleanCorruptedText(planData.goal_statement || ""));
        }
        if (stateSetters.setTargetStatement) {
          stateSetters.setTargetStatement(cleanCorruptedText(planData.target_statement || ""));
        }
        if (stateSetters.setObjectivesStatement) {
          stateSetters.setObjectivesStatement(
            Array.isArray(objectives) ? objectives.map((obj) => cleanCorruptedText(obj)) : objectives
          );
        }
        if (stateSetters.setDeliverablesStatement) {
          stateSetters.setDeliverablesStatement(cleanCorruptedText(deliverables));
        }
        if (stateSetters.setMetricsStatement) {
          stateSetters.setMetricsStatement(cleanCorruptedText(metrics));
        }
        if (stateSetters.setStakeholdersCustomersInvolvement) {
          stateSetters.setStakeholdersCustomersInvolvement(cleanCorruptedText(planData.stakeholders_customers_involvement || ""));
        }
        if (stateSetters.setExperienceDesignPrinciples) {
          stateSetters.setExperienceDesignPrinciples(cleanCorruptedText(planData.experience_design_principles || ""));
        }
        if (stateSetters.setSelectedPrograms) {
          stateSetters.setSelectedPrograms(programs);
        }
        if (stateSetters.setFinalProgramSequence) {
          stateSetters.setFinalProgramSequence(programs);
        }
        if (stateSetters.setTotalDuration) {
          stateSetters.setTotalDuration(calculatedTotalDuration || planData.total_duration || 0);
        }
        if (stateSetters.setStrategyLifecycleStatus) {
          stateSetters.setStrategyLifecycleStatus(planData.strategy_lifecycle_status || "exploring");
        }
        
        // Load enabled features (capability configuration)
        if (planData.enabled_features && stateSetters.setEnabledFeatures) {
          try {
            const loadedFeatures = JSON.parse(planData.enabled_features);
            if (Array.isArray(loadedFeatures)) {
              stateSetters.setEnabledFeatures(loadedFeatures);
              console.log("📊 Loaded enabled features from database:", loadedFeatures);
              
              // FIX #28 + #36: CRITICAL - Update BOTH uiConfig.enabledFeatures AND uiConfig.views
              // Without views, the tab filter in PlanPage strips the buttons when switching conversations
              // Map feature IDs to view IDs (config.id from Core Features Registry)
              const featureToViewMap = {
                'step_by_step_view': 'stepByStep',
                'calendar_view': 'calendar',
                'readme_view': 'readme',
                'slick_view': 'slick',
                'sow_view': 'sow',
                'strategy_view': 'strategy',
              };
              
              const viewKeys = loadedFeatures
                .map(featureId => featureToViewMap[featureId] || featureId)
                .filter(Boolean);
              
              if (stateSetters.setUIConfig) {
                stateSetters.setUIConfig(prev => {
                  if (!prev) return prev;
                  const currentViews = Array.isArray(prev.views) ? prev.views : [];
                  const mergedViews = [...new Set([...currentViews, ...viewKeys])];
                  console.log("📊 FIX #36: Updating uiConfig from database:", { 
                    enabledFeatures: loadedFeatures,
                    views: mergedViews 
                  });
                  return {
                    ...prev,
                    enabledFeatures: loadedFeatures,
                    views: mergedViews
                  };
                });
              }
            }
          } catch (e) {
            console.warn("Failed to parse enabled_features:", e);
          }
        }
        
        // Restore workflow_id to plan state
        if (planData.workflow_id && stateSetters.setCurrentWorkflowId) {
          stateSetters.setCurrentWorkflowId(planData.workflow_id);
          console.log("📊 Restored workflow_id:", planData.workflow_id);
        }
        if (stateSetters.setIsSaved) {
          stateSetters.setIsSaved(true);
        }

        // Only mark as strategy created if we actually have programs or meaningful content
        hasGoodData = programs.length > 0 || planData.short_summary;
        if (stateSetters.setHasStrategyBeenCreated && hasGoodData) {
          stateSetters.setHasStrategyBeenCreated(true);
        }
        if (stateSetters.setPlanState) {
          stateSetters.setPlanState("saved");
        }

        console.log("Plan loaded successfully:", {
          programsLoaded: programs.length,
          totalDuration: planData.total_duration || 0,
          hasContent: !!(planData.short_summary || programs.length > 0),
        });

        // Update last loaded plan ID - CRITICAL FIX
        setLastLoadedPlanId(currentPlanId);
        
        // FIX #42: Mark forceReload as consumed for this plan to prevent repeated reloads
        if (location?.state?.forceReload) {
          setConsumedForceReloadForPlan(currentPlanId);
          console.log("FIX #42: Consumed forceReload for plan", currentPlanId);
        }

        // Set load timestamp to prevent duplicate loads
        window[`planLoadTime_${currentPlanId}`] = Date.now();

        // Clear the preventAutoSave flag after successful load - only if it exists
        if (location?.state?.preventAutoSave) {
          // Use a timeout to prevent immediate state changes that could trigger reloads
          setTimeout(() => {
            window.history.replaceState(
              { ...location.state, preventAutoSave: false },
              document.title
            );
          }, 100);
        }

      } catch (error) {
        console.error("Error loading plan data:", error);
        if (stateSetters.setPlanState) {
          stateSetters.setPlanState("error");
        }
        // Reset to safe state on error
        if (stateSetters.setSelectedPrograms) stateSetters.setSelectedPrograms([]);
        if (stateSetters.setFinalProgramSequence) stateSetters.setFinalProgramSequence([]);
        if (stateSetters.setTotalDuration) stateSetters.setTotalDuration(0);
      } finally {
        setIsLoading(false);
        // Re-enable auto-save after load completes - CRITICAL FIX
        // Do this immediately, not with setTimeout, to prevent auto-save blocking
        console.log("Re-enabling auto-save after plan load, planState:", planState);
        if (stateSetters.setIsInitialLoad) {
          stateSetters.setIsInitialLoad(false);
          console.log("✅ setIsInitialLoad(false) called from usePlanDataLoader");
        }
        if (stateSetters.setPlanLoadInitialized) {
          stateSetters.setPlanLoadInitialized(true);
          console.log("✅ setPlanLoadInitialized(true) called from usePlanDataLoader");
        }
        
        // Let the PlanPage auto-save effect handle marking as unsaved when user makes changes
        console.log("✅ Plan loaded successfully - auto-save will be enabled by PlanPage after delay");
        
        console.log("AUTO-SAVE RE-ENABLED after plan load");
      }
    };

    loadPlanData();
  }, [
    currentPlanId,
    isCreatingStrategy, 
    isStrategyCreationMode, 
    planState,
    planLoadInitialized,
    hasStrategyBeenCreated,
    shortSummary,
    finalProgramSequence?.length,
    location?.state?.preventAutoSave,
    lastLoadedPlanId,
    location?.state,
    stateSetters
    // Note: Added required dependencies while maintaining loop prevention logic
  ]);

  return {
    isLoading,
  };
};
